import { css, styled } from 'styled-components';

import { textColors } from '../../theme/palette';

import type { HeadingProps, HeadingVariant } from './types';

const stylesByVariant: Record<HeadingVariant, ReturnType<typeof css>> = {
  'heading-1': css`
    font-weight: ${({ theme }) => theme.designTokens.heading1FontWeight};
    font-size: ${({ theme }) => theme.designTokens.heading1FontSize};
    line-height: ${({ theme }) => theme.designTokens.heading1LineHeight};
  `,
  'heading-2': css`
    font-weight: ${({ theme }) => theme.designTokens.heading2FontWeight};
    font-size: ${({ theme }) => theme.designTokens.heading2FontSize};
    line-height: ${({ theme }) => theme.designTokens.heading2LineHeight};
  `,
  'heading-3': css`
    font-weight: ${({ theme }) => theme.designTokens.heading3FontWeight};
    font-size: ${({ theme }) => theme.designTokens.heading3FontSize};
    line-height: ${({ theme }) => theme.designTokens.heading3LineHeight};
  `,
  'heading-4': css`
    font-weight: ${({ theme }) => theme.designTokens.heading4FontWeight};
    font-size: ${({ theme }) => theme.designTokens.heading4FontSize};
    line-height: ${({ theme }) => theme.designTokens.heading4LineHeight};
  `,
  'heading-4-light': css`
    font-weight: ${({ theme }) => theme.designTokens.heading4LightFontWeight};
    font-size: ${({ theme }) => theme.designTokens.heading4LightFontSize};
    line-height: ${({ theme }) => theme.designTokens.heading4LightLineHeight};
  `,
  'heading-5': css`
    font-weight: ${({ theme }) => theme.designTokens.heading5FontWeight};
    font-size: ${({ theme }) => theme.designTokens.heading5FontSize};
    line-height: ${({ theme }) => theme.designTokens.heading5LineHeight};
  `,
  'heading-5-light': css`
    font-weight: ${({ theme }) => theme.designTokens.heading5LightFontWeight};
    font-size: ${({ theme }) => theme.designTokens.heading5LightFontSize};
    line-height: ${({ theme }) => theme.designTokens.heading5LightLineHeight};
  `,
  subtitle: css`
    font-weight: ${({ theme }) => theme.designTokens.headingSubtitleFontWeight};
    font-size: ${({ theme }) => theme.designTokens.headingSubtitleFontSize};
    line-height: ${({ theme }) => theme.designTokens.headingSubtitleLineHeight};
  `,
  'subtitle-light': css`
    font-weight: ${({ theme }) => theme.designTokens.headingSubtitleLightFontWeight};
    font-size: ${({ theme }) => theme.designTokens.headingSubtitleLightFontSize};
    line-height: ${({ theme }) => theme.designTokens.headingSubtitleLightLineHeight};
  `,
};

const propsToOmitInDOM: Set<keyof HeadingProps> = new Set([
  'variant',
  'textAlign',
  'textDecoration',
  'textOverflow',
  'textTransform',
]);

const Div = styled.div.withConfig({
  shouldForwardProp: prop => !propsToOmitInDOM.has(prop as keyof HeadingProps),
})<HeadingProps>`
  margin: 0;
  overflow: ${({ overflow }) => overflow};
  font: inherit;
  display: ${({ display }) => display};
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.designTokens.headingFontFamily};
  color: ${({ theme, color }) => (color ? textColors[color] : theme.designTokens.headingTextColor)};
  letter-spacing: ${({ theme }) => theme.designTokens.headingLetterSpacing};
  text-align: ${({ textAlign: align }) => align};
  text-decoration: ${({ textDecoration: decoration }) => decoration};
  text-overflow: ${({ textOverflow }) => textOverflow};
  ${({ textOverflow }) =>
    (textOverflow === 'clip' || textOverflow === 'ellipsis') &&
    `
      white-space: nowrap;
      overflow-x: hidden;
    `}
  text-rendering: optimizeLegibility;
  text-transform: ${({ textTransform }) => textTransform};
  ${({ variant }) => stylesByVariant[variant]}
`;

export const Styled = {
  Div,
};
